/**
 * WelcomePage — B-2026-0078
 *
 * A minimal "You've joined [Channel Name]!" landing page shown immediately
 * after invitation redemption.  Displayed only once:
 *   - Backend sets InvitationRedeemResponse.first_time_user = true when the
 *     user has no prior evaluations.
 *   - Frontend stores a localStorage flag to prevent re-showing on refresh.
 *
 * Routing: /welcome?channel=<slug>&name=<display_name>
 *
 * The page is reachable from the invitation redemption flow in channelApi.ts /
 * App.tsx.  It is NOT behind a conditional render gating on first_time_user —
 * the URL itself is only navigated to when first_time_user is true.
 */

import { useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  SparklesIcon,
  PlayCircleIcon,
  ChartBarIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** localStorage key: marks that the athlete has seen the welcome page. */
export const WELCOME_SEEN_KEY = 'sf_welcome_page_seen';

// ---------------------------------------------------------------------------
// Step card
// ---------------------------------------------------------------------------

interface WorkflowStepProps {
  icon: React.ReactNode;
  step: number;
  title: string;
  description: string;
}

function WorkflowStep({ icon, step, title, description }: WorkflowStepProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        flex: '1 1 0',
        minWidth: '160px',
        maxWidth: '220px',
        padding: '24px 16px',
        backgroundColor: 'var(--color-dark-elevated-1)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '16px',
        position: 'relative',
      }}
    >
      {/* Step number badge */}
      <div
        style={{
          position: 'absolute',
          top: '-10px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          backgroundColor: 'var(--color-accent-blue)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: 700,
          color: '#fff',
        }}
      >
        {step}
      </div>
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '14px',
          backgroundColor: 'rgba(61, 169, 252, 0.12)',
          border: '1px solid rgba(61, 169, 252, 0.22)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '12px',
          color: 'var(--color-accent-blue)',
        }}
      >
        {icon}
      </div>
      <p
        style={{
          fontFamily: 'var(--font-heading)',
          fontWeight: 700,
          fontSize: '14px',
          color: 'var(--color-text-primary)',
          margin: '0 0 6px',
        }}
      >
        {title}
      </p>
      <p
        style={{
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {description}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function WelcomePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const channelSlug = params.get('channel') ?? '';
  const channelName = params.get('name') ?? 'your channel';
  const coachName = params.get('coach') ?? 'your coach';

  // Mark as seen on mount so subsequent visits skip to dashboard.
  useEffect(() => {
    try {
      localStorage.setItem(WELCOME_SEEN_KEY, '1');
    } catch {
      // localStorage unavailable — silently continue
    }
  }, []);

  function handleBrowseTemplates() {
    if (channelSlug) {
      navigate(`/channels/${channelSlug}`);
    } else {
      navigate('/channels');
    }
  }

  return (
    <>
      <style>{`
        @keyframes wlc-fade-up {
          from { opacity: 0; transform: translateY(18px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .wlc-page  { animation: wlc-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .wlc-s1    { animation: wlc-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.07s both; }
        .wlc-s2    { animation: wlc-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.15s both; }
        .wlc-s3    { animation: wlc-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.23s both; }
        .wlc-cta-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .wlc-cta-btn:hover {
          box-shadow: 0 0 22px rgba(30,215,96,0.35), 0 4px 12px rgba(0,0,0,0.25);
          transform: translateY(-1px);
        }
        .wlc-cta-btn:active { transform: scale(0.98); }
      `}</style>

      <div
        className="wlc-page"
        style={{
          maxWidth: '640px',
          margin: '0 auto',
          padding: '48px 24px 64px',
          textAlign: 'center',
        }}
      >
        {/* Hero icon */}
        <div className="wlc-s1" style={{ marginBottom: '28px' }}>
          <div
            style={{
              position: 'relative',
              display: 'inline-block',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: '-24px',
                borderRadius: '50%',
                background:
                  'radial-gradient(circle, rgba(30, 215, 96, 0.12) 0%, transparent 65%)',
              }}
            />
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '22px',
                backgroundColor: 'rgba(30, 215, 96, 0.10)',
                border: '1px solid rgba(30, 215, 96, 0.22)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <SparklesIcon
                style={{
                  width: '36px',
                  height: '36px',
                  color: 'var(--color-accent)',
                }}
              />
            </div>
          </div>
        </div>

        {/* Headline */}
        <div className="wlc-s1" style={{ marginBottom: '10px' }}>
          <h1
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: '28px',
              color: 'var(--color-text-primary)',
              letterSpacing: '-0.5px',
              margin: 0,
            }}
          >
            You&apos;ve joined{' '}
            <span style={{ color: 'var(--color-accent)' }}>{channelName}</span>
          </h1>
        </div>
        <p
          className="wlc-s1"
          style={{
            fontSize: '15px',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.6,
            maxWidth: '460px',
            margin: '0 auto 40px',
          }}
        >
          Welcome! {coachName !== 'your coach' ? coachName : 'Your coach'} uses AI to analyse
          your movement and give you precise form feedback. Here&apos;s how it works:
        </p>

        {/* 3-step workflow */}
        <div
          className="wlc-s2"
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            flexWrap: 'wrap',
            marginBottom: '44px',
          }}
        >
          <WorkflowStep
            step={1}
            icon={<PlayCircleIcon style={{ width: '24px', height: '24px' }} />}
            title="Upload a video"
            description="Record a 10–30 second clip of your movement, then upload it."
          />
          <WorkflowStep
            step={2}
            icon={<SparklesIcon style={{ width: '24px', height: '24px' }} />}
            title="AI scores your form"
            description="Our AI compares your technique against a reference template, frame by frame."
          />
          <WorkflowStep
            step={3}
            icon={<LightBulbIcon style={{ width: '24px', height: '24px' }} />}
            title="See coaching feedback"
            description="Get specific tips on what to improve and track your progress over time."
          />
        </div>

        {/* CTAs */}
        <div
          className="wlc-s3"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '14px',
          }}
        >
          <button
            onClick={handleBrowseTemplates}
            className="wlc-cta-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 32px',
              borderRadius: '999px',
              backgroundColor: 'var(--color-accent)',
              border: 'none',
              color: 'var(--color-text-inverse)',
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '15px',
              cursor: 'pointer',
            }}
          >
            <ChartBarIcon style={{ width: '18px', height: '18px' }} />
            Browse Templates
          </button>

          <Link
            to="/dashboard"
            style={{
              fontSize: '13px',
              color: 'var(--color-text-muted)',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color =
                'var(--color-text-secondary)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.color =
                'var(--color-text-muted)';
            }}
          >
            Skip to Dashboard
          </Link>
        </div>
      </div>
    </>
  );
}
